import { CSSProperties, RefObject, useCallback, useState } from 'react';
import { PlacementType } from '../types';
import { getPosition } from '../Utils/getPosition';

interface Position {
  top: number;
  left: number;
}

interface TooltipPosition {
  arrowPosition: Position;
  tooltipStyle: CSSProperties;
  updatePosition: () => void
}

const useTooltipPositioning = (
  tooltipRef: RefObject<HTMLElement>,
  anchorRef: RefObject<HTMLElement> | undefined,
  placement: PlacementType,
  containerRef?: RefObject<HTMLElement>,
  absolutePosition?: { top: string; left: string }
): TooltipPosition => {
  const [position, setPosition] = useState<Position>({ top: -1000, left: -1000 });
  const [arrowPosition, setArrowPosition] = useState({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    if (!anchorRef && !absolutePosition) return;

    if (anchorRef) {
      const result = getPosition(
        anchorRef.current,
        tooltipRef.current,
        placement,
        containerRef?.current || document.body
      );

      setPosition(result.position);
      setArrowPosition(result.arrowPosition);
    }
  }, [tooltipRef, anchorRef, placement, containerRef, absolutePosition]);

  // Create tooltip style based on absolute position or calculated position
  const tooltipStyle: CSSProperties = absolutePosition
    ? { 
        position: 'absolute', 
        top: absolutePosition.top, 
        left: absolutePosition.left 
      }
    : { 
        position: 'absolute', 
        top: position.top + 'px', 
        left: position.left + 'px',
      };

  return { arrowPosition, tooltipStyle, updatePosition };
};

export default useTooltipPositioning; 